import type { TFunction } from 'i18next';

/** Código de app que as exclusões de tenant devolvem com cobrança viva (#789). */
const LIVE_GATEWAY_BILLING = 'live_gateway_billing';

/**
 * Texto do erro de uma exclusão de tenant (encerrar ou excluir definitivamente).
 *
 * Origem: #789 — 2026-10-01 10:40 +0200.
 *
 * Por quê: `hardDeleteEmptyTenant` e `requestTenantDeletion` recusam com
 * `failed-precondition` e mensagem `live_gateway_billing` quando a assinatura
 * no gateway ainda cobra. Mostrar o código cru no toast não diz ao operador o
 * que fazer.
 *
 * Contrato (puro): erro do callable com `message === 'live_gateway_billing'`
 * vira, com o provider de `details.provider`,
 * `tenantDetail.actions.liveGatewayBillingPurge` na exclusão definitiva (que
 * não tenta parar o gateway: manda encerrar antes) ou
 * `tenantDetail.actions.liveGatewayBilling` no encerramento (que tentou e não
 * conseguiu); outro `Error` mantém a própria mensagem; o resto vira
 * `tenantDetail.actions.errorGeneric`.
 *
 * Usado por: `HardDeleteTenantDialog`, `DeleteTenantDialog` (aba "Excluir" do
 * `TenantDetailPage`).
 *
 * Dados — erro do callable (FirebaseError do SDK de Functions):
 *   { "code": "functions/failed-precondition", "message": "live_gateway_billing",
 *     "details": { "code": "live_gateway_billing", "provider": "asaas", "livemode": true,
 *                  "externalSubscriptionId": "sub_000000000000", ... } }
 * Fonte: `liveGatewayBillingError` em `apps/functions/src/lib/billing/liveGatewayBilling.ts`.
 */
export function tenantDeletionErrorMessage(err: unknown, t: TFunction, action: 'close' | 'purge'): string {
  if (err instanceof Error && err.message === LIVE_GATEWAY_BILLING) {
    const details = (err as { details?: { provider?: unknown } }).details;
    const provider = typeof details?.provider === 'string' ? details.provider : '—';
    // Auditoria 2026-10-01 (#789, B5): o purge não tenta parar o gateway — a
    // mensagem dele manda encerrar antes; a do encerramento diz que parar falhou.
    return t(
      action === 'purge' ? 'tenantDetail.actions.liveGatewayBillingPurge' : 'tenantDetail.actions.liveGatewayBilling',
      { provider },
    );
  }
  return err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric');
}
